<template>
  <div class="cms-form-section">
    <div 
      v-if="title || note || $slots.header || collapsible" 
      class="cms-section-header" 
      :class="{ 
        'has-content-open': !collapsible || (collapsible && isOpen),
        'is-collapsible': collapsible
      }"
      @click="collapsible ? toggle() : null"
    >
      <div class="cms-section-header-left">
        <div v-if="collapsible" class="cms-section-toggle">
          <v-icon :icon="isOpen ? 'chevron-down' : 'chevron-right'" size="sm" />
        </div>
        <div>
          <h3 v-if="title" class="cms-section-title">{{ title }}</h3>
          <p v-if="note" class="cms-section-note">{{ note }}</p>
        </div>
      </div>
      <div class="cms-section-header-right" @click.stop>
        <slot name="header"></slot>
      </div>
    </div>
    <transition name="cms-accordion">
      <div v-show="!collapsible || isOpen" class="cms-section-content" :class="{ 'has-grid': grid }">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface Props {
  title?: string;
  note?: string;
  grid?: boolean;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  grid: false,
  collapsible: true,
  defaultOpen: true
});

const isOpen = ref(props.defaultOpen);

const toggle = () => {
  if (props.collapsible) {
    isOpen.value = !isOpen.value;
  }
};

// Watch for changes in defaultOpen prop
watch(() => props.defaultOpen, (newValue) => {
  isOpen.value = newValue;
});
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-form-section {
  margin-bottom: $cms-spacing-md;
  padding: $cms-spacing-md $cms-spacing-lg;
  border-radius: $cms-radius-xl;
  background-color: $cms-background-white;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.cms-section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $cms-spacing-md;
  margin-bottom: 0;

  &.has-content-open {
    margin-bottom: $cms-spacing-lg;
  }

  &.is-collapsible {
    cursor: pointer;
    user-select: none;

    &:hover {
      .cms-section-title {
        color: $cms-primary-color;
      }
    }
  }
}

.cms-section-header-right {
  flex-shrink: 0;
}

.cms-section-header-left {
  display: flex;
  align-items: flex-start;
  gap: $cms-spacing-sm;
  flex: 1;
}

.cms-section-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;
  margin-top: 0.125rem;
  border-radius: $cms-radius-sm;
  transition: all $cms-transition-base;
  color: $cms-text-secondary;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: $cms-text-primary;
  }

  :deep(.icon) {
    transition: transform $cms-transition-base;
  }
}

.cms-section-title {
  font-size: $cms-font-size-md;
  font-weight: 500;
  margin: 0;
  color: $cms-text-primary;
}

.cms-section-note {
  color: $cms-text-secondary;
  font-style: italic;
  margin: 0.5rem 0 0 0;
  font-size: $cms-font-size-base;
}

.cms-section-content {
  &.has-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: $cms-spacing-md;
  }
}

// Accordion transition
.cms-accordion-enter-active,
.cms-accordion-leave-active {
  transition: all $cms-transition-base;
  overflow: hidden;
}

.cms-accordion-enter-from {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

.cms-accordion-enter-to {
  opacity: 1;
  max-height: 5000px;
}

.cms-accordion-leave-from {
  opacity: 1;
  max-height: 5000px;
}

.cms-accordion-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}
</style>



